import { SafeError } from '@benzinga/safe-await';
import { ExtendedSubscribable } from '../subscribable';
import { SocketStateMachine, SocketV2State } from './stateMachine';
import { DEFAULT_RETRY_CONFIG, RetryConfig } from './retryPolicy';
import { ReconnectionController } from './reconnection';
import { SendPolicy, SendQueue, SocketData } from './sendQueue';
import { Heartbeat } from './heartbeat';
import { DEFAULT_NETWORK_CONFIG, NetworkConfig, NetworkMonitor } from './networkMonitor';
import { SocketTransport } from './transport';
import { SocketStatsTracker } from './stats';
import {
  DEFAULT_CONNECT_TIMEOUT_MS,
  SocketLogger,
  SocketStats,
  SocketV2Config,
  SubscribableSocketV2Event,
  UrlProvider,
  isFatalCloseCodeDefault,
} from './types';

export type { UrlProvider, SocketLogger, SocketStats, SocketV2Config, SubscribableSocketV2Event } from './types';

interface SocketV2Functions<RESP, REQ> {
  close: SubscribableSocketV2<RESP, REQ>['close'];
  open: SubscribableSocketV2<RESP, REQ>['open'];
  reconnect: SubscribableSocketV2<RESP, REQ>['reconnect'];
  send: SubscribableSocketV2<RESP, REQ>['send'];
  sendObject: SubscribableSocketV2<RESP, REQ>['sendObject'];
}

/**
 * Orchestrator. Owns the state machine, the transport, the heartbeat, the send queue and the
 * network monitor, and is the only thing that dispatches events. Retry/backoff policy lives in
 * ReconnectionController and the counters live in SocketStatsTracker — both are reached only
 * through their own methods, never by poking at their state.
 */
export class SubscribableSocketV2<RESP = unknown, REQ = unknown> extends ExtendedSubscribable<
  SubscribableSocketV2Event<RESP>,
  SocketV2Functions<RESP, REQ>
> {
  private stateMachine: SocketStateMachine;
  private reconnection: ReconnectionController;
  private statsTracker: SocketStatsTracker;
  private queue: SendQueue;
  private heartbeat: Heartbeat<RESP, REQ>;
  private networkConfig: NetworkConfig;
  private network: NetworkMonitor;
  private logger?: SocketLogger;
  private urlProvider: UrlProvider;
  private config: SocketV2Config<RESP, REQ>;
  private transport: SocketTransport<RESP>;

  constructor(url: UrlProvider, config: SocketV2Config<RESP, REQ> = {}) {
    super();
    this.urlProvider = url;
    this.config = config;
    this.logger = config.logger;
    const retryConfig: RetryConfig = { ...DEFAULT_RETRY_CONFIG, ...config.retry };
    this.networkConfig = { ...DEFAULT_NETWORK_CONFIG, ...config.network };
    this.stateMachine = new SocketStateMachine((from, to) => this.log('error', 'illegal-transition', { from, to }));
    this.statsTracker = new SocketStatsTracker();
    this.reconnection = new ReconnectionController(retryConfig, {
      connect: () => this.connectAttempt(),
      dispatch: event => this.dispatch(event),
      getState: () => this.stateMachine.state,
      isFatalCloseCode: (code: number) =>
        this.config.fatalCloseCodes !== undefined
          ? this.config.fatalCloseCodes.includes(code)
          : isFatalCloseCodeDefault(code),
      isOnline: () => this.network.isOnline(),
      log: (level, event, meta) => this.log(level, event, meta),
      onOutageStarted: () => this.statsTracker.onDropped(),
      retryAfterFromClose: (event: CloseEvent) => this.config.retryAfterFromClose?.(event),
      transition: (to: SocketV2State) => this.stateMachine.transition(to),
    });
    this.queue = new SendQueue(config.sendQueue?.maxSize, () => this.dispatch({ type: 'queue-overflow' }));
    this.heartbeat = new Heartbeat<RESP, REQ>(config.heartbeat ?? {}, {
      onDead: () => this.reconnect(),
      sendPing: frame => this.send(typeof frame === 'string' ? frame : JSON.stringify(frame), { policy: 'drop' }),
    });
    this.network = new NetworkMonitor(this.networkConfig, {
      onOffline: this.handleOffline,
      onOnline: this.handleOnline,
      onPageHide: this.handlePageHide,
      onWake: this.handleWake,
    });
    this.transport = new SocketTransport<RESP>(
      this.urlProvider,
      this.config.binaryType ?? 'blob',
      {
        onConnectTimeout: () => {
          // The attempt is already aborted inside the transport; announce it and fall into the
          // normal drop machinery so the usual backoff (or offline suspend) applies.
          this.log('warn', 'connect-timeout');
          this.statsTracker.setLastError('connect timeout');
          this.dispatch({ errorEvent: new SafeError('socket connect timeout', 'socketV2'), type: 'error' });
          // A live socket arms this same watchdog on browser `offline`. If we are still
          // offline when it fires, suspend; if online returned, leave the connection up.
          if (this.stateMachine.state === 'open') {
            if (!this.network.isOnline()) {
              this.suspendForOfflineNow();
            }
            return;
          }
          this.handleDrop(undefined);
        },
        onError: (error: SafeError, lastErrorTag: string, attemptAborted: boolean) => {
          this.statsTracker.setLastError(lastErrorTag);
          this.dispatch({ errorEvent: error, type: 'error' });
          if (attemptAborted) {
            this.handleDrop(undefined);
          }
        },
        onHandshakeFailed: (event: CloseEvent) => {
          this.handleDrop(event);
        },
        onLiveClosed: (event: CloseEvent) => {
          this.heartbeat.stop();
          // public close() uses closeGracefully (it never lands here). Every live
          // close is unexpected — including Chrome DevTools Offline's clean 1000
          // and pagehide. Parking in terminal 'closed' would ignore a later online.
          this.dispatch({ event, type: 'close' });
          this.handleDrop(event);
        },
        onMessage: (data: RESP) => {
          this.heartbeat.recordActivity(data);
          this.dispatch({ msg: data, type: 'response' });
        },
        onOpened: () => {
          this.statsTracker.onConnected();
          this.stateMachine.transition('open');
          const { info, reconnected } = this.reconnection.onConnected();
          this.dispatch(reconnected ? { info, type: 'reconnected' } : { type: 'open' });
          this.heartbeat.start();
          // Resubscribe-before-replay: managers send their resubscribes inside the
          // reconnected/open handler above; only then replay what was queued.
          this.queue.drain().forEach(data => this.send(data));
        },
        onWarn: (event: string) => this.log('warn', event),
        shouldProceed: () => {
          const state = this.stateMachine.state;
          if (state !== 'connecting' && state !== 'reconnecting') return false;
          if (state === 'reconnecting') {
            // 'reconnecting' -> 'open' is not a legal transition, but 'reconnecting' -> 'connecting'
            // is; normalize here so onopen's unconditional transition('open') below never throws.
            this.stateMachine.transition('connecting');
          }
          return true;
        },
      },
      this.config.connectTimeoutMs ?? DEFAULT_CONNECT_TIMEOUT_MS,
    );
  }

  public getState = (): SocketV2State => this.stateMachine.state;

  public getStats = (): SocketStats => this.statsTracker.snapshot(this.stateMachine.state, this.queue.depth);

  public open = (): void => {
    const state = this.stateMachine.state;
    if (state !== 'idle' && state !== 'closed' && state !== 'failed') {
      return;
    }
    this.reconnection.reset();
    this.network.start();
    if (this.stateMachine.transition('connecting')) {
      void this.connectAttempt();
    }
  };

  public reconnect = (): void => {
    const state = this.stateMachine.state;
    if (state === 'closing' || state === 'closed' || state === 'idle' || state === 'failed') {
      return;
    }
    this.log('info', 'forced-reconnect');
    this.teardownTransport();
    this.handleDrop(undefined);
  };

  public close = (): void => {
    this.reconnection.reset();
    this.heartbeat.stop();
    this.network.stop();
    this.queue.clear();
    this.statsTracker.endOutage();
    const state = this.stateMachine.state;
    if (state === 'idle' || state === 'closed' || state === 'failed') {
      // 'failed' is terminal until open() is called again (only 'connecting' is legal from
      // here) — closing is a no-op, not an illegal-transition throw.
      return;
    }
    if (state === 'open' && this.transport.isLive()) {
      this.stateMachine.transition('closing');
      this.dispatch({ type: 'closing' });
      this.transport.closeGracefully(event => {
        // Guard against a stale/late close event arriving after open() has already
        // reconnected — without this, a real WebSocket's async close firing after the
        // replacement connection is up would look like the *new* connection died.
        if (!this.transport.isLive() && !this.transport.hasPending() && this.stateMachine.state === 'closed') {
          this.dispatch({ event, type: 'close' });
        }
      });
      this.stateMachine.transition('closed');
      return;
    }
    // connecting / reconnecting / suspended: abort quietly, v1-style handler nulling.
    this.transport.abortPending();
    this.stateMachine.transition('closed');
  };

  public sendObject = <T = REQ>(data: T, options?: { policy?: SendPolicy }): SafeError | undefined =>
    this.send(JSON.stringify(data), options);

  public send = (data: SocketData, options?: { policy?: SendPolicy }): SafeError | undefined => {
    const policy: SendPolicy = options?.policy ?? 'queue';
    if (this.stateMachine.state === 'open' && this.transport.isLive()) {
      try {
        this.transport.sendRaw(data);
        this.dispatch({ msg: data, type: 'request' });
        return undefined;
      } catch (err) {
        const safeError = new SafeError('socket send failed', 'socketV2', err);
        this.statsTracker.setLastError('send failed');
        this.dispatch({ errorEvent: safeError, type: 'error' });
        return safeError;
      }
    }
    switch (policy) {
      case 'queue':
        this.queue.enqueue(data);
        return undefined;
      case 'drop':
        return undefined;
      case 'error':
        return new SafeError(`socket not open (state: ${this.stateMachine.state})`, 'socketV2');
    }
  };

  protected onSubscribe(): SocketV2Functions<RESP, REQ> {
    return {
      close: this.close,
      open: this.open,
      reconnect: this.reconnect,
      send: this.send,
      sendObject: this.sendObject,
    };
  }

  protected onZeroSubscriptions(): void {
    this.close();
  }

  // ---- internal: connection lifecycle -------------------------------------------------

  private connectAttempt = (): void => {
    void this.transport.connect();
  };

  /**
   * Single entry point for "the transport is gone". The heartbeat is socket-owned, so it is
   * stopped here before the reconnection policy takes over (calling stop() twice is a no-op).
   */
  private handleDrop = (closeEvent: CloseEvent | undefined): void => {
    this.heartbeat.stop();
    this.reconnection.handleDrop(closeEvent);
  };

  private teardownTransport = (): void => {
    // Abort whichever socket exists without letting its onclose double-drive handleDrop.
    if (this.transport.isLive()) this.heartbeat.stop();
    this.transport.teardown();
  };

  // ---- internal: network signals ------------------------------------------------------

  private handleOffline = (): void => {
    const state = this.stateMachine.state;
    if (state === 'suspended') {
      this.reconnection.markSuspendedOffline();
      return;
    }
    if (state !== 'open' && state !== 'connecting' && state !== 'reconnecting') {
      return;
    }
    // Live sockets reuse the connect watchdog instead of a second timer. connectTimeoutMs: 0
    // disables it, so offline then tears down immediately (same as a hung handshake).
    if (state === 'open' && this.transport.isLive()) {
      const timeoutMs = this.config.connectTimeoutMs ?? DEFAULT_CONNECT_TIMEOUT_MS;
      if (timeoutMs > 0) {
        this.log('info', 'offline-wait-timeout', { timeoutMs });
        this.transport.armConnectTimer();
        return;
      }
    }
    this.suspendForOfflineNow();
  };

  private suspendForOfflineNow = (): void => {
    const state = this.stateMachine.state;
    if (state !== 'open' && state !== 'connecting' && state !== 'reconnecting') {
      return;
    }
    this.log('info', 'offline');
    this.reconnection.cancel();
    this.teardownTransport();
    // Downtime starts accruing, but this is not a drop — dropCount must not move.
    this.statsTracker.startOutage();
    this.reconnection.suspendForOffline();
  };

  private handleOnline = (): void => {
    if (this.stateMachine.state === 'open') {
      this.transport.clearConnectTimer();
    }
    // Only the offline reason resumes here: a flap cooldown is also parked in
    // 'suspended', but its timer owns the exit — an online blip must not defeat it.
    if (this.stateMachine.state !== 'suspended' || !this.reconnection.canResumeFromOffline()) {
      return;
    }
    this.log('info', 'online');
    this.reconnection.resumeFromOffline();
  };

  private handleWake = (): void => {
    const state = this.stateMachine.state;
    if (state === 'open' || state === 'connecting') {
      this.log('info', 'wake-reconnect');
      this.reconnect();
    } else if (state === 'suspended' && this.reconnection.canResumeFromOffline() && this.network.isOnline()) {
      this.handleOnline();
    }
  };

  private handlePageHide = (): void => {
    if (this.stateMachine.state === 'open' && this.transport.isLive()) {
      this.log('info', 'pagehide-close');
      if (!this.transport.requestCleanClose()) {
        this.log('warn', 'pagehide-close-failed');
      }
      // Do not tear down our own state here: if the page survives (bfcache),
      // onLiveClosed routes the eventual close through handleDrop.
    }
  };

  private log = (level: 'debug' | 'info' | 'warn' | 'error', event: string, meta?: Record<string, unknown>): void => {
    // NEVER pass URLs, tokens, or payloads into meta.
    this.logger?.({ event, level, meta, state: this.stateMachine.state });
  };
}
